import { OnInit, Component, ChangeDetectorRef, ViewChild } from '@angular/core';
import { filterPageTypes, toggleFilterTypes } from 'src/app/store/filters.era';
import { AllSameKeysToTypeEnforcer, loadLocal } from '../../helpers/helpers';
import { tableFilterTypes } from 'src/app/store/performers.era';
import { saveTable } from '../performers/performerSharedFunctions';
import { JoiEvent } from 'globals/store/events.data';
import { AppTableData, appTableColumns } from 'src/app/helpers/table-column-types';
import { TableColumn, TableDotMenuItem, TableMenuItem } from 'src/app/helpers/table.data';
import { NestedTableComponent } from 'src/app/components/nested-table/nested-table.component';
import { Wrapper } from 'src/app/components/wrapper.component';
import { PublishDotMenuChoices, allDefaultAppColumns, appTableColumnsById } from '../../helpers/table-helpers';
import { NewAppDialogComponent } from './new-app.dialog.component';
import { openDialogGetResult } from '../../helpers/angular-helpers';
import { Account } from 'globals/store/accounts.data';
import { appsTableReselector } from '../../store/reselectors/apps.reselector';
import { Dictionary } from '@ngrx/entity';
import { JoiApp, JoiAppInfo } from 'globals/store/app.data';
import { ConfirmDeleteData, openConfirmDeleteGetResult } from '../../components/dialog.confirmDelete.component';
import { ConfirmationDialogData, SimpleConfirmationDialogComponent } from '../../components/dialogues-general/simple-confirmation.dialog.component';
import { JoiMessageComponent } from 'src/app/components/cards/joi-message.component';
import { AppManagerComponent } from './app-manager.component';
import { LoaderPaneFragmentComponent } from 'src/app/components/loader/loader-pane-fragment.component';
import { CommonModule, DatePipe } from '@angular/common';
import { CommandMenuComponent } from 'src/app/components/nav-bar/command-menu.component';
import { traceNames } from 'src/app/services/performance.tracking.data';

@Component({
    selector: 'app-table',
    template: `
<loader-pane-fragment [trace]="traceNames.viewEventApps" ></loader-pane-fragment>
<ng-container *ngIf="!selectedAppId">
  <command-menu
    [filterPage]="filterPage"
    [filterType]="filterType"
    [showDottedMenu]="false"
    [canEditColumns]="false"
    [addItemText]="!event || event.is_readonly ? '' : '+ CREATE APP'"
    (addItem)="addApp($event)"
  ></command-menu>
  <joi-message *ngIf="message" [message]="message"></joi-message>
  <nested-table
    [filtersId]="filterPage"
    [items]="rows || []"
    [readOnly]="!event || event.is_readonly"
    [condensedView]="condensed"
    [columns]="columns"
    [columnsById]="columnsById"
    [menuItems]="menuItems"
    [dotMenuItems]="dotMenuItems"
    [emptyMessage]="'No apps in this event'"
    [noResultsMessage]="'All apps have been filtered out'"
    [defaultSelectedColumnId]="defaultColumn"
    (selectItem)="openApp($event.app)"
    (updatedSelectedIds)="selectIds($event)"
  ></nested-table>
</ng-container>
<app-manager 
  *ngIf="!!selectedAppId" [appId]="selectedAppId" [startMobile]="startMobile"
  (close)="selectedAppId = null"
></app-manager>
`,
    styleUrls: ['../../page.scss'],
    standalone: true,
    providers: [DatePipe],
    imports: [JoiMessageComponent, NestedTableComponent, AppManagerComponent, LoaderPaneFragmentComponent, CommonModule, CommandMenuComponent],
})
export class AppTableComponent extends Wrapper<AppTableComponent> implements OnInit {
  filterPage = filterPageTypes.appTable
  filterType = tableFilterTypes.appTable
  defaultColumn = appTableColumns.name
  rows: AppTableData[] = []
  traceNames = traceNames
  columns = allDefaultAppColumns
  columnsById: { [id: string]: TableColumn } = appTableColumnsById
  rowsById: { [id:string]: AppTableData } = {}
  message = ''
  MESSAGE_DISPLAY_MS = 5000
  dotMenuItems: AllSameKeysToTypeEnforcer<Omit<PublishDotMenuChoices, 'edit' | 'duplicate'>, TableDotMenuItem> = {
    update: { id: 'update', title: 'Update', method: this.updateSingleApp.bind(this) },
    view: { id: 'view', title: 'View', method: this.openAppFromTable.bind(this) },
    delete: { id: 'delete', title: 'Delete', method: this.deleteSingleApp.bind(this) },
  }
  startMobile: boolean

  deleteSingleApp(row: AppTableData, event) {
    this.deleteApps([row], event)
  }

  updateSingleApp(row: AppTableData, event) {
    this.updateApps([row])
  }

  openAppFromTable(row: AppTableData) {
    this.openApp(row.app)
  }


  condensed: boolean
  @ViewChild(NestedTableComponent) nestedTable: NestedTableComponent<AppTableData>
  selectedIds: string[] 
  event: JoiEvent
  account: Partial<Account>
  loadedTableDataOnce: boolean
  rowCountBeforeFiltering: number
  selectedAppId: string

  canUpdateCount = 0
  menuItems: TableMenuItem[] = []

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super()
    this.uiService.changePageTitle("Apps", true)
    this.loadCondensed()
    this.subProp(this.store.select('events', 'event'), 'event')
    this.subProp(this.store.select('accounts', 'account'), 'account')
    this.subCallback(this.store.select('filters', 'toggleFilters', this.filterPage, 'condensed'), _ => this.loadCondensed())
  }

  ngOnInit() {
    this.selectCallback(appsTableReselector, null, ({ rows, rowsById, rowCountBeforeFiltering }) => {
      this.rows = rows
      this.rowsById = rowsById
      this.rowCountBeforeFiltering = rowCountBeforeFiltering
      this.loadedTableDataOnce = true
    })
 
  }
  
  saveTable() {
    saveTable('apps' ,`${this.event.title} apps.csv`, this.dialog, this.nestedTable)
  }

  async openApp(app: JoiAppInfo) {
    this.startMobile = window.innerWidth <= 768
    this.selectedAppId = app.id
  }

  loadCondensed() {
    this.condensed = loadLocal(`${this.filterPage}:${toggleFilterTypes.condensed}`, false) 
    // console.log(this.condensed)
  }

  async addApp(event) {
    console.log("Should be adding an app!", event)
    await openDialogGetResult(this.dialog, NewAppDialogComponent, { width: '480px', disableClose: true })
  }

  async deleteApps(rows: AppTableData[], event) {
    const n = rows.length
    const plural = n === 1 ? '' : 's'
    const data: ConfirmDeleteData = {
      title: `Delete App${plural}`,
      rect: event.target.getBoundingClientRect(),
      para: `Are you sure you want to delete ${n === 1 ? 'this' : 'these ' + n} app${plural}?  This action cannot be undone`,
      deleteButtonText: 'DELETE',
      dialog: this.dialog
    }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return
    try {
      await Promise.all(rows.map(r => this.appService.deleteApp(r.app)))
      this.setMessage(`${n} app${plural} ${n === 1 ? 'was' : 'were'} deleted.`)
    } catch (error) {
      console.log(error)
    }
  }
  
  setMessage(message: string) {
    this.message = message
    setTimeout(() => this.message = '', this.MESSAGE_DISPLAY_MS)
  }

  async updateApps(rows: AppTableData[]) {
    const n = rows.length
    const plural = n === 1 ? '' : 's'
    const data: ConfirmationDialogData = {
      confirmText: 'UPDATE NOW',
      title: `Update App${plural}`,
      description: `${n} app${plural} will be updated immediately.`
    }
    const confirmed = await this.openDialogGetResult(SimpleConfirmationDialogComponent, { width: '480px', disableClose: true, data })
    if (!confirmed) return
    try {
      await Promise.all(rows.map(r => this.appService.updateAppFromTable(r.app)))
      this.setMessage(`${n} app${plural} ${n === 1 ? 'was' : 'were'} updated successfully.`)
    } catch (error) {
      console.log(error)
    }
  }

  selectIds(ids: Set<string>) {
    console.log("Select ids called!", ids)
    setTimeout(() => {
      this.menuItems = []
      this.menuItems.push({ title: 'Delete', method: this.deleteApps.bind(this) }) 
      this.canUpdateCount = Array.from(ids).reduce((tot, id) => tot + (this.rowsById[id].app.status === 'published' ? 1 : 0), 0)
      console.log({ canUpdateCount: this.canUpdateCount, nIds: ids.size })
      if (this.canUpdateCount && this.canUpdateCount === ids.size) this.menuItems.unshift({ title: 'Update', method: this.updateApps.bind(this) })
    }, 0)
  }


}
